import { PositionType } from "./types";

export function getRandomWindowPosition(
  centerPosition: PositionType
): PositionType {
  // Define the maximum offset range (adjust these values as needed)
  const maxOffsetX = 100; // Maximum 100px offset horizontally
  const maxOffsetY = 200; // Maximum 80px offset vertically

  // Generate random offsets that can be positive or negative
  const randomOffsetX = (Math.random() - 0.5) * 2 * maxOffsetX; // Range: -100 to +100
  // Bias the Y offset to favor upward movement (-80 to +40 range)
  const randomOffsetY = (Math.random() - 0.95) * 2 * maxOffsetY; // Range: -80 to +40

  return {
    x: Math.max(0, centerPosition.x + randomOffsetX),
    y: Math.max(0, centerPosition.y + randomOffsetY),
  };
}

/**
 * Calculates the center position for a window based on viewport dimensions
 * @param windowWidth - The width of the window to be centered
 * @param windowHeight - The height of the window to be centered
 * @returns PositionType object with x and y coordinates for centering the window
 */
export const calculateCenterPosition = (
  windowWidth: number = 400,
  windowHeight: number = 300
): PositionType => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Calculate center position by offsetting by half the window size
  const centerX = viewportWidth * 0.5 - windowWidth * 0.5;
  const centerY = viewportHeight * 0.5 - windowHeight * 0.5;

  return {
    x: Math.max(0, centerX),
    y: Math.max(0, centerY),
  };
};

/**
 * Calculates a position offset from center
 * @param windowWidth - The width of the window
 * @param windowHeight - The height of the window
 * @param offsetX - Horizontal offset from center (positive = right, negative = left)
 * @param offsetY - Vertical offset from center (positive = down, negative = up)
 * @returns PositionType object with adjusted coordinates
 */
export const calculateOffsetPosition = (
  windowWidth: number = 400,
  windowHeight: number = 300,
  offsetX: number = 0,
  offsetY: number = 0
): PositionType => {
  const centerPosition = calculateCenterPosition(windowWidth, windowHeight);

  return {
    x: Math.max(0, centerPosition.x + offsetX),
    y: Math.max(0, centerPosition.y + offsetY),
  };
};
